// karyawan.repo — master pegawai (employees). Port KARYAWAN v0.18.
// Password/login di-provision via Supabase Auth (auth_id) — TIDAK disimpan di sini.
import { supabase } from '@core/supabase';
import type { Role, Outlet } from '@core/types';

export interface KaryawanRow {
  id: number; username: string; email: string | null; nama: string; role: Role; outlet: string | null;
  scope: string | null; wa: string | null; aktif: boolean; level: string | null;
  lahir: string | null; rekening: string | null; bawahan: number[];
  suspended_by: string | null; suspended_at: string | null;
  komisi_type: string | null; komisi_val: number | null;
  /** Sudah punya akun login (auth_id terisi). UUID-nya sendiri sengaja TIDAK dibawa ke UI. */
  punyaAkun: boolean;
}

const SEL = 'id,username,email,nama,role,outlet,scope,wa,aktif,level,lahir,rekening,bawahan,suspended_by,suspended_at,komisi_type,komisi_val,auth_id';

interface Raw { bawahan: unknown; auth_id?: unknown; [k: string]: unknown }
const normalize = (r: Raw): KaryawanRow => {
  const { auth_id, ...rest } = r;
  return { ...(rest as unknown as KaryawanRow), bawahan: Array.isArray(r.bawahan) ? (r.bawahan as number[]) : [], punyaAkun: !!auth_id };
};

/** Hasil provisioning — password HANYA ada di memori sesaat, tampil sekali, tak pernah disimpan/di-log. */
export interface AkunHasil { action: 'create' | 'reset'; email: string; password: string; nama: string }

/**
 * Panggil Edge Function `provision-account` (satu-satunya pemegang service-role key).
 * JWT pemakai ikut otomatis lewat functions.invoke → server yang memutuskan boleh/tidak.
 */
export async function provisionAccount(employeeId: number, action: 'create' | 'reset', password?: string): Promise<AkunHasil> {
  const body: { employeeId: number; action: string; password?: string } = { employeeId, action };
  if (password) body.password = password; // admin-set (opsional); kosong → EF buat acak
  const { data, error } = await supabase().functions.invoke('provision-account', { body });
  if (error) {
    // Error asli dari Edge Function ada di BODY respons (error.context = Response), bukan error.message.
    // Baca sbg text lalu parse — kalau body sudah dikonsumsi/kosong, tetap tampilkan status + message
    // supaya penyebab sebenarnya kelihatan (bukan pesan "belum deploy" yang menyesatkan saat fungsi ADA).
    const ctx = (error as { context?: Response }).context;
    let detail: string | undefined;
    let status: number | undefined;
    if (ctx && typeof ctx.text === 'function') {
      status = ctx.status;
      try { const t = await ctx.text(); detail = (JSON.parse(t) as { error?: string })?.error ?? (t || undefined); } catch { /* body tak terbaca */ }
    }
    throw new Error(detail ?? `Gagal memanggil layanan akun${status ? ` (HTTP ${status})` : ''}: ${(error as Error).message || 'periksa fungsi provision-account'}`);
  }
  const res = data as { ok?: boolean; error?: string } & AkunHasil;
  if (!res?.ok) throw new Error(res?.error ?? 'Gagal memproses akun.');
  return res;
}

export async function listKaryawan(): Promise<KaryawanRow[]> {
  const { data, error } = await supabase().from('employees').select(SEL).order('nama');
  if (error) throw new Error(error.message);
  return ((data ?? []) as Raw[]).map(normalize);
}

export async function listOutlets(): Promise<Outlet[]> {
  const { data, error } = await supabase().from('outlets').select('kode,nama').order('kode');
  if (error) throw new Error(error.message);
  return (data ?? []) as Outlet[];
}

export async function usernameTaken(u: string, exceptId?: number): Promise<boolean> {
  const { data, error } = await supabase().from('employees').select('id').eq('username', u);
  if (error) throw new Error(error.message);
  return ((data ?? []) as { id: number }[]).some((e) => e.id !== exceptId);
}

/** Email sudah dipakai karyawan lain? Email SELALU disimpan lowercase → `.eq` (exact, tanpa wildcard,
 *  aman dari pola). Backstop lintas-cabang (di luar read-scope SPV) = unique index uq_employees_email. */
export async function emailTaken(email: string, exceptId?: number): Promise<boolean> {
  const { data, error } = await supabase().from('employees').select('id').eq('email', email);
  if (error) throw new Error(error.message);
  return ((data ?? []) as { id: number }[]).some((e) => e.id !== exceptId);
}

export async function createKaryawan(d: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().from('employees').insert(d); if (error) throw new Error(error.message);
}
export async function updateKaryawan(id: number, d: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().from('employees').update(d).eq('id', id); if (error) throw new Error(error.message);
}
export async function removeKaryawan(id: number): Promise<void> {
  const { error } = await supabase().from('employees').delete().eq('id', id); if (error) throw new Error(error.message);
}

// ---- Akun login SUPPLIER (employees role='Supplier' + supplier_id) ----
export interface SupAkun { supplier_id: number; emp_id: number; username: string; has_auth: boolean }
/** Status akun login tiap supplier → dipetakan by supplier_id di UI Pengaturan→Supplier. */
export async function listSupplierAccounts(): Promise<SupAkun[]> {
  const { data, error } = await supabase().from('employees').select('id,username,supplier_id,auth_id').eq('role', 'Supplier');
  if (error) throw new Error(error.message);
  return ((data ?? []) as { id: number; username: string; supplier_id: number | null; auth_id: string | null }[])
    .filter((e) => e.supplier_id != null)
    .map((e) => ({ supplier_id: e.supplier_id as number, emp_id: e.id, username: e.username, has_auth: e.auth_id != null }));
}
/** Pastikan ada baris employees untuk supplier (idempotent, tak menimpa). Return id-nya. */
export async function ensureSupplierEmployee(supplierId: number, nama: string, username: string): Promise<number> {
  const { data: ex, error: e0 } = await supabase().from('employees').select('id').eq('role', 'Supplier').eq('supplier_id', supplierId).maybeSingle();
  if (e0) throw new Error(e0.message);
  if (ex) return (ex as { id: number }).id;
  if (await usernameTaken(username)) throw new Error(`Username "${username}" sudah dipakai. Pilih yang lain.`);
  const { data, error } = await supabase().from('employees')
    .insert({ username, nama, role: 'Supplier', outlet: null, scope: 'Sendiri', supplier_id: supplierId, aktif: true })
    .select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: number }).id;
}

/** Tiket aktif (belum Sudah Diambil) yang masih atas nama teknisi ini. */
export async function activeTicketCount(nama: string): Promise<number> {
  const { count, error } = await supabase().from('tickets')
    .select('no', { count: 'exact', head: true })
    .eq('teknisi', nama).neq('status', 'Sudah Diambil');
  if (error) throw new Error(error.message);
  return count ?? 0;
}
export async function ownerCount(): Promise<number> {
  const { count, error } = await supabase().from('employees')
    .select('id', { count: 'exact', head: true }).eq('role', 'Owner');
  if (error) throw new Error(error.message);
  return count ?? 0;
}
